import type { DisplaySamplesResponse } from '#constants/types/norce';
import useApiFetch from '~/composables/useApiFetch';
import { useGlobalContentStore } from '~/store/globalContent';

const MAX_PART_NOS = 100;
const BATCH_DELAY_MS = 30;
const RETRY_DELAY_MS = 15000;
const MAX_RETRIES = 4;

// Shared by every component on the page, so a product list asks for all its products in one request
const requested = new Set<string>();
let queue: string[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
let retries = 0;

/**
 * Which stores have a product on display.
 * Part numbers are collected for a moment and sent together to /retailers/display-samples.
 *
 * samples[partNo] is undefined until loaded, a list of store ids (same as accountid in /retailers)
 * when loaded and null if the request failed or the backend has no index yet (refreshedAt is null).
 * An answer without an index is not remembered, it is asked again after a while (a few times) and on the next request.
 */
export default function useDisplaySamples() {
  const globalContent = useGlobalContentStore();
  const { apiGet } = useApiFetch();
  const samples = useState<Record<string, string[] | null>>('displaySamples', () => ({}));

  const flush = async() => {
    timer = undefined;
    const partNos = queue;
    queue = [];
    const dataAreaId = globalContent.domainMap?.dataAreaId;

    if (!dataAreaId) {
      partNos.forEach(partNo => requested.delete(partNo));
      return;
    }

    for (let i = 0; i < partNos.length; i += MAX_PART_NOS) {
      const chunk = partNos.slice(i, i + MAX_PART_NOS);
      const res = await apiGet<DisplaySamplesResponse>(
        `/retailers/display-samples?dataAreaId=${dataAreaId}&partNo=${chunk.map(encodeURIComponent).join(',')}`
      ).catch(() => null);

      const ready = !!res?.refreshedAt;

      if (ready) {
        retries = 0;
      } else {
        chunk.forEach(partNo => requested.delete(partNo));
        if (retries < MAX_RETRIES) {
          retries++;
          setTimeout(() => chunk.forEach(queuePartNo), RETRY_DELAY_MS * retries);
        }
      }

      samples.value = {
        ...samples.value,
        ...Object.fromEntries(chunk.map(partNo => [partNo, (ready && res?.displaySamples?.[partNo]) || null])),
      };
    }
  };

  function queuePartNo(partNo: string) {
    if (!partNo || requested.has(partNo) || !import.meta.client) {
      return;
    }
    requested.add(partNo);
    queue.push(partNo);
    timer ??= setTimeout(flush, BATCH_DELAY_MS);
  }

  // A new page or product list starts with a fresh set of retries
  const request = (partNo: string) => {
    retries = 0;
    queuePartNo(partNo);
  };

  return { samples, request };
}
